import React, { createContext, useContext, useEffect, useState } from 'react'
import type { User } from '@supabase/supabase-js'
import { authService } from '@/services/auth'
import { supabase } from '@/services/supabase'
import { useNavigate } from 'react-router-dom'
import { checkAdminPanelAccess, getUserDefaultRoute, userRoleCache } from '@/utils/roleCache'

interface AuthContextType {
  user: User | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error?: { message: string } }>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<{ error?: { message: string } }>
  updatePassword: (newPassword: string) => Promise<{ error?: { message: string } }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    // Check for existing session
    const checkSession = async () => {
      try {
        const currentUser = await authService.getUser()
        if (currentUser) {
          // Verify admin panel access (super admin, admin, or marketer with blog permissions)
          const hasAccess = await checkAdminPanelAccess(supabase, currentUser.id)

          if (hasAccess) {
            setUser(currentUser)
          } else {
            await authService.signOut()
            navigate('/login')
          }
        }
      } catch (error) {
        console.error('Session check error:', error)
      } finally {
        setLoading(false)
      }
    }

    checkSession()

    // Subscribe to auth state changes
    const subscription = authService.onAuthStateChange(async (user) => {
      if (user) {
        // Verify admin panel access on auth state change
        const hasAccess = await checkAdminPanelAccess(supabase, user.id)

        if (hasAccess) {
          setUser(user)
        } else {
          setUser(null)
          await authService.signOut()
          navigate('/login')
        }
      } else {
        setUser(null)
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [navigate])

  const signIn = async (email: string, password: string) => {
    const response = await authService.signIn(email, password)
    if (response.data) {
      setUser(response.data)
      // Determine default route based on user permissions
      const defaultRoute = await getUserDefaultRoute(supabase, response.data.id)
      navigate(defaultRoute)
    }
    return { error: response.error }
  }

  const signOut = async () => {
    // Clear role cache when signing out
    userRoleCache.clearAll()
    await authService.signOut()
    setUser(null)
    navigate('/login')
  }

  const resetPassword = async (email: string) => {
    const response = await authService.resetPassword(email)
    return { error: response.error }
  }

  const updatePassword = async (newPassword: string) => {
    const response = await authService.updatePassword(newPassword)
    return { error: response.error }
  }

  const value = {
    user,
    loading,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}